import { Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AvailableLanguage,
  ItAvatarDirective,
  ItDropdownComponent,
  ItDropdownItemComponent,
  ItHeaderComponent,
  ItNavBarItemComponent,
  ItTooltipDirective,
} from 'design-angular-kit';
import { environment } from '../../../environments/environment';
import { HeaderLink } from '../../shared/interfaces/header-link';
import { User } from '../../shared/interfaces/user.interface';
import { AuthService } from '../../shared/services/auth.service';
import { UtilsService } from '../../shared/services/utils.service';
import { SocialsComponent } from '../socials/socials.component';

@Component({
  selector: 'drw-header',
  imports: [
    ItHeaderComponent,
    TranslatePipe,
    ItNavBarItemComponent,
    ItDropdownComponent,
    ItDropdownItemComponent,
    ItDropdownComponent,
    ItDropdownItemComponent,
    ItAvatarDirective,
    ItTooltipDirective,
    TranslatePipe,
    SocialsComponent,
  ],
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _utilsService = inject(UtilsService);
  public readonly translateService = inject(TranslateService);

  protected membershipOrganization: { title: string | undefined; url: string | undefined } = {
    title: environment.membershipOrganization.title ?? undefined,
    url: environment.membershipOrganization.url ?? undefined,
  };

  protected links: Signal<HeaderLink[]> = computed(() => {
    return this.mapLinks();
  });

  availableLanguages: AvailableLanguage[] = [
    { code: 'it', label: 'Italiano' },
    { code: 'en', label: 'English' },
  ];

  user: Signal<User | null> = toSignal(this._authService.currentUser$, { initialValue: null });
  hasUser = computed(() => Boolean(this.user()));

  hasProjectId: Signal<boolean> = computed(() => Boolean(this._utilsService.projectId()));

  userInitials = computed(() => {
    const currentUser = this.user();

    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser?.firstName.at(0)}${currentUser?.lastName.at(0)}`;
    }

    return 'U';
  });

  logoRedirectUrl = computed<string>(() => {
    return this._authService.isAdminOrSuperAdmin() ? '/admin-panel/homepage' : '/projects';
  });

  constructor() {
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.setLanguage(event.lang);
    });
  }

  setLanguage(lang: string) {
    this.translateService.use(lang);
    this._utilsService.setLanguage(lang);
  }

  /**
   * Logout
   */
  onLogout() {
    void this._router.navigate(['/auth/login']);
    localStorage.clear();
    this._authService.setCurrentUser(null);
  }

  linksBySection = computed(() => {
    return {
      adminPanel: [
        {
          title: 'HEADER.LINKS.ADMIN_PANEL.TITLE',
          url: 'admin-panel',
          canShow: true,
          children: [
            {
              title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.PROJECTS_MONITORING',
              url: `projects`,
            },
            {
              title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.REMODULED_ECONOMIC_FRAMEWORK',
              url: `admin-panel/projects/remoduled-economic-framework`,
            },
            {
              title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.VARIANTS',
              url: `admin-panel/projects/variants`,
            },
            {
              title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.PROJECTS',
              url: `admin-panel/projects/management`,
            },
            {
              title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.FUNDING_LINES_MANAGEMENT',
              url: `admin-panel/funding-lines`,
            },
           ...(this._authService.isSuperAdmin()
              ? [{
                  title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.CONFIGURATOR',
                  url: 'admin-panel/configurator',
                },
                {
                  title: 'HEADER.LINKS.ADMIN_PANEL.CHILDREN.DXC_INBOUND_REQUESTS',
                  url: 'admin-panel/dxc-inbound-requests',
                }]
              : []),
          ],
        },
      ],
      projectsMonitoring: [
        {
          title: 'HEADER.LINKS.STRUCTURE.TITLE',
          url: '#',
          canShow:
            this.hasProjectId() && !this._utilsService.currentUrl()?.includes('/admin-panel'),
          children: [
            {
              title: 'HEADER.LINKS.STRUCTURE.CHILDREN.DEFINITION',
              url: `projects/${this._utilsService.projectId()}/structure/definition`,
            },
            {
              title: 'HEADER.LINKS.STRUCTURE.CHILDREN.BUILDINGS_INVOLVED',
              url: `projects/${this._utilsService.projectId()}/structure/buildings`,
            },
            {
              title: 'HEADER.LINKS.STRUCTURE.CHILDREN.INTERVENTIONS',
              url: `projects/${this._utilsService.projectId()}/structure/interventions`,
            },
            {
              title: 'HEADER.LINKS.STRUCTURE.CHILDREN.COSTS_ALLOCATION',
              url: `projects/${this._utilsService.projectId()}/structure/costs-allocation`,
            },
            {
              title: 'HEADER.LINKS.STRUCTURE.CHILDREN.ECONOMIC_FRAMEWORK',
              url: `projects/${this._utilsService.projectId()}/structure/economic-framework`,
              disabled: !this._utilsService.isCostsAllocationCompiled(),
              tooltipText: 'NOTIFICATIONS.COSTS_ALLOCATION_NOT_COMPILED',
            },
          ],
        },
        {
          title: 'HEADER.LINKS.PROJECT_MANAGEMENT.TITLE',
          url: '#',
          canShow:
            this.hasProjectId() && !this._utilsService.currentUrl()?.includes('/admin-panel'),
          disabled: !this._utilsService.isEconomicFrameworkValidated(),
          tooltipText: 'HEADER.LINKS.PROJECT_MANAGEMENT.TOOLTIP_TEXT',
          children: [
            {
              title: 'HEADER.LINKS.PROJECT_MANAGEMENT.CHILDREN.WORKING_SCHEDULE',
              url: `projects/${this._utilsService.projectId()}/management/working-schedule`,
            },
            {
              title: 'HEADER.LINKS.PROJECT_MANAGEMENT.CHILDREN.WORKS_CALLS',
              url: `projects/${this._utilsService.projectId()}/management/works-calls`,
            },
            {
              title: 'HEADER.LINKS.PROJECT_MANAGEMENT.CHILDREN.ENG_ARCH_CALLS',
              url: `projects/${this._utilsService.projectId()}/management/eng-arch-calls`,
            },
            {
              title: 'HEADER.LINKS.PROJECT_MANAGEMENT.CHILDREN.REMODULED_ECONOMIC_FRAMEWORK',
              url: `projects/${this._utilsService.projectId()}/management/remoduled-economic-framework`,
              disabled: !this._utilsService.isRemoduledEconomicFrameworkValid(),
              tooltipText: 'NOTIFICATIONS.QER_NOT_ACCESSIBLE',
            },
            {
              title: 'HEADER.LINKS.PROJECT_MANAGEMENT.CHILDREN.VARIANTS',
              url: `projects/${this._utilsService.projectId()}/management/variants`,
              disabled: !this._utilsService.isRemoduledEconomicFrameworkValidated(),
              tooltipText: 'NOTIFICATIONS.QER_NOT_VALIDATED',
            },
          ],
        },
        {
          title: 'HEADER.LINKS.FINANCIAL_MANAGEMENT',
          url: null,
          canShow: this.hasUser(),
          disabled: true,
        },
        { title: 'HEADER.LINKS.OPERATING_MANUAL', url: null, canShow: this.hasUser() },
        { title: 'HEADER.LINKS.NOTICES', url: null, canShow: this.hasUser() },
      ],
    };
  });

  mapLinks(): HeaderLink[] {
    if (this._authService.isAdminOrSuperAdmin()) {
      return [...this.linksBySection().adminPanel, ...this.linksBySection().projectsMonitoring];
    } else {
      return this.linksBySection().projectsMonitoring;
    }
  }
}
